import { useEffect, useCallback, useMemo, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import { InputContainer } from "uikit-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";
import { AutoComplete, Input, Select } from "antd";

import { Form, FormRow, FormInputWrapper } from "../../../../ui";
import { AppContexts } from "../../../../providers";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  location_type: yup.string().required("Type is required"),
  property_id: yup.string(),
});

const defaultValues = {
  name: "",
  code: "",
  location_type: "warehouse", // warehouse | property_closet | linen_room | garage | other
  property_name: "",
  address: "",
  usage: [], // housekeeping, maintenance, owner_supplies, guest_amenities
  notes: "",
  active: true,
  property_id: "",
};

const LOCATION_TYPE_OPTIONS = [
  { label: "Warehouse", value: "warehouse" },
  { label: "Property Closet", value: "property_closet" },
  { label: "Linen Room", value: "linen_room" },
  { label: "Garage / Shed", value: "garage" },
  { label: "Other", value: "other" },
];

const USAGE_OPTIONS = [
  { label: "Housekeeping", value: "housekeeping" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Owner Supplies", value: "owner_supplies" },
  { label: "Guest Amenities", value: "guest_amenities" },
];

const StorageLocationAdd = ({
  location,          // existing location object or null
  flagIsNew,         // boolean: true for add, false for edit
  flagIsProcessing,  // boolean for spinner
  accountId,         // user.account_id (pass from parent)
  onSave,            // (formData) => Promise|void
  onCancel,          // () => void
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isDirty },
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const { settings } = useContext(AppContexts.SettingsContext);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoadingProps(true);

        // First try JSON (if you want parity with TaskAdd)
        const res = await fetch("/sample_calendar_data.json");
        const data = await res.json();
        const propsFromJson = (data?.properties ?? []).map((p) => ({
          label: p.label || p.name || p.id,
          value: String(p.id),
          address: p.address || "",
        }));

        // Fallback to settings.homes if JSON empty or missing
        const fallbackFromSettings = (settings?.homes ?? []).map((h) => ({
          label: h.label || h.name || h.id,
          value: String(h.id),
          address: h.address || "",
        }));

        const finalOpts = propsFromJson.length ? propsFromJson : fallbackFromSettings;

        if (!cancelled) setPropertyOptions(finalOpts);
      } catch (e) {
        console.error("Error loading properties in StorageLocationAdd:", e);
        const fallbackFromSettings = (settings?.homes ?? []).map((h) => ({
          label: h.label || h.name || h.id,
          value: String(h.id),
          address: h.address || "",
        }));
        if (!cancelled) setPropertyOptions(fallbackFromSettings);
      } finally {
        if (!cancelled) setLoadingProps(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [settings?.homes]);

  const usage = useMemo(() => getValues("usage"), [watch("usage")]);
  const active = useMemo(() => getValues("active"), [watch("active")]);
  const locationType = watch("location_type");
  const propertyId = watch("property_id");

  const clearForms = useCallback(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
  }, [setValue]);

  const usageOptions = useMemo(
    () =>
      USAGE_OPTIONS.map((opt) => ({
        label: opt.label,
        value: opt.value,
      })),
    []
  );

  const usageList = useMemo(() => {
    const current = getValues("usage") || [];
    return usageOptions.filter((o) => current.includes(o.value));
  }, [watch("usage"), usageOptions, getValues]);

  useEffect(() => {
    clearErrors();

    const src = location || {};
    Object.keys(defaultValues).forEach((formname) => {
      setValue(
        formname,
        src[formname] !== undefined ? src[formname] : defaultValues[formname]
      );
    });

    if (!location) {
      clearForms();
    }
  }, [location, clearErrors, setValue, clearForms]);

  const toggleUsage = useCallback(
    (value) => {
      const current = getValues("usage") || [];
      const has = current.includes(value);
      const next = has
        ? current.filter((u) => u !== value)
        : [...current, value];
      setValue("usage", next);
    },
    [getValues, setValue]
  );

  const handleRegister = useCallback(
    async (formdata) => {
      const payload = {
        ...formdata,
        account_id: accountId,
        id: location?.id, // keep id when editing
      };

      try {
        const result = await (onSave ? onSave(payload) : true);
        if (result !== false) {
          UIkit.offcanvas("#modal-storage-location-add").hide();
          reset(defaultValues);
        }
      } catch (err) {
        UIkit.notification("Failed to save storage location.", "error");
      }
    },
    [accountId, location, onSave, reset]
  );

  const handleError = useCallback((err) => {
    console.log(err);
  }, []);

  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    UIkit.offcanvas("#modal-storage-location-add").hide();
  }, [onCancel]);

  return (
    <div
      id="modal-storage-location-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={handleClose}
          ></button>

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {flagIsNew ? "Add Storage Location" : "Edit Storage Location"}
            </h2>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <Form
                onSubmit={handleSubmit(handleRegister, handleError)}
                data-uk-grid="true"
              >
                <input
                  type="hidden"
                  {...register("account_id", {
                    value: accountId,
                  })}
                />

                {/* Name + Code */}
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Location Name*</label>
                  <input
                    className={classnames("uk-input", {
                      error: errors.name,
                    })}
                    type="text"
                    {...register("name")}
                  />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Type</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <select
                        {...register("location_type")}
                      >
                        {LOCATION_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className={classnames("uk-button uk-button-default", {
                          "uk-form-danger": errors.location_type,
                        })}
                        type="button"
                        tabIndex="-1"
                      >
                        <span className="select-value">
                          {LOCATION_TYPE_OPTIONS.find((o) => o.value === locationType)?.label ||
                            "Select a Type"}
                        </span>
                        <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Property</label>
                  <div className="uk-form-controls">
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      placeholder={loadingProps ? "Loading properties..." : "Select a property"}
                      style={{ width: "100%" }}
                      options={propertyOptions}
                      value={propertyId || undefined}
                      onChange={(val, option) => {
                        setValue("property_id", val || "", {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setSelectedProperty(option || null);
                      }}
                    />

                    {errors.property_id && (
                      <div className="uk-text-danger uk-margin-small-top">
                        {errors.property_id.message}
                      </div>
                    )}
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Address</label>
                  <textarea
                    className="uk-textarea"
                    rows="2"
                    {...register("address")}
                  />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Usage</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <Select
                        mode="multiple"
                        labelInValue
                        optionFilterProp="label"
                        allowClear
                        style={{ width: "100%" }}
                        placeholder="Select Usage"
                        value={usageList} // array of { label, value }
                        onChange={(vals) => {
                          // vals = [{ label, value }, ...]
                          const valueArray = (vals || []).map((v) => v.value);
                          setValue("usage", valueArray, { shouldDirty: true });
                        }}
                        options={usageOptions}
                      />
                    </div>
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Notes</label>
                    <textarea
                      className="uk-textarea"
                      rows="2"
                      {...register("notes")}
                    />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Status</label>
                  <div className="uk-form-controls">
                    <div className="autosend-checkbox">
                      <label className="setting-switch">
                        <input
                          type="checkbox"
                          checked={!!active}
                          onChange={(e) =>
                            setValue("active", e.target.checked)
                          }
                        />
                        <span className="setting-slider setting-round"></span>
                      </label>
                      <span className="uk-margin-small-left">
                        {active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="submit"
                  >
                    {flagIsProcessing ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageLocationAdd;